import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { Image, TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { settings } from "@config/settings";
import classNames from "classnames";
import { localesParserDictionary } from "@utils/locales-parser";
import { EntitiesFetcherService } from "@services/entities-fetcher";
import { MainRouter } from "@config/urls";
import { useState } from "react";
import { Ensayo } from "@models/ensayo";
import styles from "./ensayo-detalle-page.module.scss";
import { useCultivos } from "src/context/useCultivos";
import { useTranslation } from "next-i18next";
import { usePaises } from "src/context/usePaises";
import Link from "next/link";
import { useProductos } from "src/context/useProductos";
import { useFamilias } from "src/context/useFamilias";
import { Indicator } from "src/presentation/components/organisms";
import { ColorSet } from "@styles/style-types";
import { EnsayoAdjuntoCard } from "src/presentation/components/organisms/ensayo-adjunto-card";
import { NextSeo } from "next-seo";

const EnsayoDetallePage: NextPage = (props: any) => {
  const { ensayoDetalle, country } = props;

  const cultivos = useCultivos();
  const paises = usePaises();
  const { t } = useTranslation();
  const [ensayoData] = useState(
    new Ensayo().deserialize(JSON.parse(ensayoDetalle))
  );

  const InfoSection = () => {
    const cultivoRelacionado = cultivos.find(
      (x) => x.id === ensayoData.cultivo
    );

    return (
      <section className={styles.infoSection}>
        <div className={styles.dataContainer}>
          <Text as="h1">{ensayoData.nombre}</Text>
          <div className={styles.propertiesContainer}>
            <div className={styles.property}>
              <TemplateText
                template="micro"
                className={classNames(styles.label)}
              >
                {t("EnsayosDetailView.cultivo")}
              </TemplateText>
              <Text as="h4" className={classNames("small", styles.cultivo)}>
                {cultivoRelacionado && (
                  <>
                    <Image
                      src={cultivoRelacionado.icono}
                      alt={cultivoRelacionado.nombre}
                    />
                    {cultivoRelacionado.nombre}
                  </>
                )}
              </Text>
            </div>
            <div className={styles.divider} />
            <div className={styles.property}>
              <TemplateText
                template="micro"
                className={classNames(styles.label)}
              >
                {t("EnsayosDetailView.realizado")}
              </TemplateText>
              <Text as="h4" className={classNames("small")}>
                {paises.find((x) => x.iso === ensayoData.pais)?.nombre}
              </Text>
            </div>

            <div className={styles.divider} />
            <div className={styles.property}>
              <TemplateText
                template="micro"
                className={classNames(styles.label)}
              >
                {t("EnsayosDetailView.productos")}
              </TemplateText>
              {ensayoData.productos.map((p) => {
                return (
                  <Link
                    legacyBehavior
                    key={p.producto.slug}
                    passHref
                    href={`/${MainRouter["producto-detalle"].generatePath({
                      id: p.producto.slug,
                      country: country,
                    })}`}
                  >
                    <a>
                      <TemplateText
                        style={{ color: p.familia.color }}
                        template="small"
                      >
                        {p.producto.nombre}
                      </TemplateText>
                    </a>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
        <div className={styles.bannerContainer}>
          <Image src={ensayoData.imagen} alt={ensayoData.nombre} />
        </div>
      </section>
    );
  };

  const ResultadosSection = () => {
    return (
      <section className={styles.resultadosSection}>
        <Text as="h2" className={styles.title}>
          {t("EnsayosDetailView.resultados")}
        </Text>
        <div className={styles.resultadosWrapper}>
          {ensayoData.resultados.map((item) => {
            return (
              <div
                className={styles.wrapper}
                key={`${item.valor}-${item.magnitud}`}
              >
                <Indicator
                  value={`${item.valor} ${item.magnitud}`}
                  description={item.descripcion}
                  animated
                />
              </div>
            );
          })}
          {ensayoData.roi > 0 && (
            <div className={styles.wrapper}>
              <Indicator
                title={t("EnsayosDetailView.roititle") || ""}
                value={`${ensayoData.roi}:1`}
                description={t("EnsayosDetailView.roidescription") || ""}
                bgcolor={ColorSet.primary}
                color={ColorSet.white}
                animated
              />
            </div>
          )}
        </div>
      </section>
    );
  };

  const AdjuntosSection = () => {
    return (
      <section className={styles.adjuntosSection}>
        {ensayoData && ensayoData.adjuntos.length > 0 && (
          <Text as="h2">{t("EnsayosDetailView.adjuntos")}</Text>
        )}

        <div className={styles.adjuntosContainer}>
          {ensayoData.adjuntos.map((item) => {
            return (
              <div key={item.descripcion} className={styles.wrapper}>
                <EnsayoAdjuntoCard
                  key={item.imagen}
                  imagen={item.imagen}
                  card
                  descripcion={item.descripcion}
                />
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <>
      <NextSeo
        title={`manvert | ${ensayoData.nombre}`}
        description={t("SEOMeta.EnsayosView.title") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.ensayos"),
            url: `/${country}${MainRouter.ensayos.generatePath()}`,
          },
          {
            label: ensayoData.nombre,
            url: MainRouter.ensayos.generatePath(),
          },
        ]}
      >
        <div className={classNames(styles.root)}>
          <InfoSection />
          <ResultadosSection />
          <AdjuntosSection />
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, res, req, params, query } = ctx;
  const ensayoID = params?.id as string;
  const host = `https://${req.headers.host}`;
  const { country } = query;

  const countryCode = country?.toString() || settings.DEFAULT_COUNTRY;

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: countryCode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    paises: true,
    ensayos: true,
    cultivos: true,
    productosDestacados: true,
    productosGenericos: true,
  });

  const result = entities.ensayosData
    ? entities?.ensayosData.find((x) => x.slug === ensayoID)
    : null;

  if (!result) {
    return {
      redirect: {
        permanent: true,
        destination: `/${countryCode}/${MainRouter.ensayos.generatePath()}`,
      },
    };
  }
  return {
    props: {
      host: host,
      country: countryCode,
      ensayoDetalle: JSON.stringify(result),
      cultivosData: JSON.stringify(entities.cultivosData),
      paisesData: JSON.stringify(entities.paisesData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default EnsayoDetallePage;
